/* eslint-disable max-lines */
import {
  calculateDataForGeneralIndicators,
  convertToMachineWorkingReport,
  getRealtimeStatusFromMachines,
  MachineWorkingReport,
  RealTimeMachines,
  RealtimeMachineStatus
} from '@models/main-dashboard';
import { getDatesFromPredefinedTimeRange } from '@utils/dates';
import { createImmerReducer } from '../../reducers/utils/createReducer';
import * as c from './types';

type Status = 'idle' | 'loading' | 'success' | 'error';

export interface RealTimeCardData {
  stopedMachines: number;
  workingMachines: number;
  noDataMachines: number;
  noShiftMachines: number;
  machines: RealtimeMachineStatus[];
}

interface MachinesListData {
  name: string;
  id: number;
  value: any;
  isNegative?: boolean;
}

export interface TotalAvailabilityData {
  availability: number;
  trend: number;
  highAvailability: MachinesListData[];
  lowAvailability: MachinesListData[];
  machines: MachinesListData[];
}

interface GeneralCauseReportItem {
  generalCauseId?: string;
  generalCause?: string;
  stopsDurationMinutes: number;
  percentage?: number;
  trend?: number;
}

interface MainStops {
  stopId: number;
  machineId: string;
  machineName: string;
  generalCauseId: string;
  generalCauseName: string;
  stopDurationMinutes: number;
}

interface GeneralCausesReport {
  stopsDurationMinutes: number;
  generalCausesItems: GeneralCauseReportItem[];
  mainStops: MainStops[];
}

interface Minutes {
  classified: number;
  notClassified: number;
}

interface ClassifiedStopsMachineItem {
  machineId: number;
  name: string;
  minutes: Minutes;
}

interface DeclaredStopsReport {
  minutesCurrent: Minutes;
  minutesComparison: Minutes;
  trend?: number;
  machines: ClassifiedStopsMachineItem[];
}

export interface MainDashboardState {
  dates: {
    from: Date;
    to?: Date;
  };
  realtime: {
    status: Status;
    error?: any;
    data?: RealTimeCardData;
    datasource?: RealTimeMachines[];
  };
  totalAvailability: {
    status: Status;
    error?: any;
    data?: TotalAvailabilityData;
    datasource?: MachineWorkingReport;
  };
  generalCauses: {
    status: Status;
    error?: any;
    data?: GeneralCausesReport;
  };
  declaredStops: {
    status: Status;
    error?: any;
    data?: DeclaredStopsReport;
  };
}

const initialState: MainDashboardState = {
  dates: {
    from: new Date(),
    to: undefined
  },
  realtime: {
    status: 'idle',
    error: undefined
  },
  totalAvailability: {
    status: 'idle',
    error: undefined
  },
  generalCauses: {
    status: 'idle',
    error: undefined
  },
  declaredStops: {
    status: 'idle',
    error: undefined
  }
};

const mainDashboardReducer = createImmerReducer(initialState, {
  [c.FETCH_MACHINE_STATUS]: (draft = initialState) => {
    draft.realtime = {
      status: 'loading'
    };
  },
  [c.FETCH_MACHINE_STATUS_SUCCESS]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.realtime = {
      status: 'success',
      datasource: payload,
      data: getRealtimeStatusFromMachines(payload)
    };
  },
  [c.FETCH_MACHINE_STATUS_SMOOTHLY_SUCCESS]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.realtime = {
      status: 'success',
      datasource: payload,
      data: getRealtimeStatusFromMachines(payload)
    };
  },
  [c.FETCH_MACHINE_STATUS_ERROR]: (draft: MainDashboardState, { payload }) => {
    draft.realtime = {
      error: payload,
      status: 'error'
    };
  },

  [c.FETCH_METRICS_MAIN_DASHBOARD]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.dates = getDatesFromPredefinedTimeRange(
      payload.range,
      payload.timezone,
      new Date()
    );
    draft.totalAvailability = {
      status: 'loading'
    };
  },
  [c.FETCH_METRICS_MAIN_DASHBOARD_SUCCESS]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    const datasource = convertToMachineWorkingReport(payload);
    draft.totalAvailability = {
      status: 'success',
      datasource,
      data: calculateDataForGeneralIndicators(datasource)
    };
  },
  [c.FETCH_METRICS_MAIN_DASHBOARD_ERROR]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.totalAvailability.status = 'error';
    draft.totalAvailability.error = payload;
  },

  [c.FETCH_GENERALCAUSES_MAIN_DASHBOARD]: (draft: MainDashboardState) => {
    draft.generalCauses = {
      status: 'loading'
    };
  },
  [c.FETCH_GENERALCAUSES_MAIN_DASHBOARD_SUCCESS]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.generalCauses = {
      status: 'success',
      data: payload
    };
  },
  [c.FETCH_GENERALCAUSES_MAIN_DASHBOARD_ERROR]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.generalCauses.status = 'error';
    draft.generalCauses.error = payload;
  },

  [c.FETCH_DECLARED_STOPS_MAIN_DASHBOARD]: (draft: MainDashboardState) => {
    draft.declaredStops = {
      status: 'loading'
    };
  },
  [c.FETCH_DECLARED_STOPS_MAIN_DASHBOARD_SUCCESS]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.declaredStops = {
      status: 'success',
      data: payload
    };
  },
  [c.FETCH_DECLARED_STOPS_MAIN_DASHBOARD_ERROR]: (
    draft: MainDashboardState,
    { payload }
  ) => {
    draft.declaredStops.status = 'error';
    draft.declaredStops.error = payload;
  }
});

export default mainDashboardReducer;
